import { useMemo } from 'react'
import * as Yup from 'yup'
import { parsePhoneNumberFromString } from 'libphonenumber-js/max'
import { useTranslation } from 'react-i18next'
import { nameRegex, emailRegex } from 'src/utils/validation'
import { BookingFor } from '@customTypes/BookingFor'
import { parse, isValid } from 'date-fns/fp'
import { AGE_LIMIT, BIRTDATE_FORMAT_DATE_FNS, BIRTHDATE_FORMAT } from '@screens/Contact/components/Birthdate/Birthdate'
import { isEmpty } from 'src/utils/isEmpty'
import { useLanguage } from './useLanguage'
import { differenceInYears } from 'date-fns'
import { TFunction } from 'i18next'
import { useIsChildProphylaxis } from './useIsChildProphylaxis'
import { useChoiceState } from '@context/ChoiceContext'

declare module 'yup' {
  interface StringSchema {
    onlyAllowedChars: (message: string) => any
    customEmail: (message: string) => any
    validateBirthdate: (message: string, data: ValidateBirthdateData) => any
  }
}

Yup.addMethod(Yup.string, 'onlyAllowedChars', function (message) {
  return Yup.string().test('onlyAllowedChars', message, function (value) {
    const { path, createError } = this

    const isValid = !nameRegex.test(value || '')

    if (isValid) {
      return true
    } else {
      return createError({ path, message })
    }
  })
})

Yup.addMethod(Yup.string, 'customEmail', function (message) {
  return Yup.string().test('customEmail', message, function (value) {
    const { path, createError } = this

    const isValid = emailRegex.test(value || '')

    if (isValid) {
      return true
    } else {
      return createError({ path, message })
    }
  })
})

export enum CustomMessageErrors {
  ChildProphylaxisAgeError = 'ChildProphylaxisAgeError',
}

interface ValidateBirthdateData {
  birthdateFormat: string
  t: TFunction
  bookingFor: BookingFor
  isChildProphylaxis: boolean
  dateOfAppointment: Date
}

Yup.addMethod(Yup.string, 'validateBirthdate', function (message, validateBirthdateData: ValidateBirthdateData) {
  return Yup.string().test('validateBirthdate', message, function (value) {
    const { path, createError } = this
    const { t, birthdateFormat, bookingFor, isChildProphylaxis, dateOfAppointment } = validateBirthdateData

    // Is empty
    if (isEmpty(value) || value === birthdateFormat) {
      return createError({ path, message: t(DATE_VALIDATION_TRANSLATIONS.EMPTY) })
    }

    // Is not valid format
    const date = parse(new Date())(BIRTDATE_FORMAT_DATE_FNS.UI)(value)
    if (!isValid(date)) {
      return createError({ path, message: t(DATE_VALIDATION_TRANSLATIONS.INVALID_FORMAT) })
    }

    const now = new Date()

    // Child Prophylaxis validation
    if (isChildProphylaxis) {
      const ageOfPatientInTheAppointmentDay = differenceInYears(dateOfAppointment, date)
      if (ageOfPatientInTheAppointmentDay < 6 || ageOfPatientInTheAppointmentDay > 17)
        return createError({
          path,
          message: CustomMessageErrors.ChildProphylaxisAgeError,
          params: { childProphylaxisError: true },
        })
    }

    // Regular patient age validation
    const ageOfPatient = differenceInYears(now, date)

    // Is Older than MAX years
    const maxAge = AGE_LIMIT[bookingFor].MAX
    if (ageOfPatient > maxAge) {
      return createError({ path, message: t(DATE_VALIDATION_TRANSLATIONS.TOO_OLD) })
    }

    // Is Younger than MIN years
    const minAge = AGE_LIMIT[bookingFor].MIN
    if (ageOfPatient < minAge) {
      return createError({ path, message: t(DATE_VALIDATION_TRANSLATIONS.TOO_YOUNG[bookingFor]) })
    }

    return true
  })
})

type UseContactFormValidationProps = {
  isManualReviewRequired?: boolean
}

const useContactFormValidation = ({ isManualReviewRequired }: UseContactFormValidationProps = {}) => {
  const { t } = useTranslation()
  const lang = useLanguage()
  const { isChildProphylaxis } = useIsChildProphylaxis()
  const birthdateFormat = BIRTHDATE_FORMAT[lang]
  const { selectedBookableDateAndTime } = useChoiceState()
  const { originalStartTimestamp } = selectedBookableDateAndTime || {}

  const validation = useMemo(() => {
    const dateOfAppointment = new Date(originalStartTimestamp)
    return Yup.object().shape({
      bookingFor: Yup.string(),
      gender: Yup.string().oneOf(['female', 'male', 'diverse'], t('gender_required')),
      firstName: Yup.string()
        .onlyAllowedChars(t('invalid_char'))
        .required(t('first_name_required'))
        .min(2, t('field_min_length', { length: 2 }))
        .max(100, t('field_max_length', { length: 100 }))
        .nullable(),
      lastName: Yup.string()
        .onlyAllowedChars(t('invalid_char'))
        .required(t('last_name_required'))
        .min(2, t('field_min_length', { length: 2 }))
        .max(100, t('field_max_length', { length: 100 }))
        .nullable(),
      patient_firstName: Yup.string()
        .onlyAllowedChars(t('invalid_char'))
        .when('bookingFor', {
          is: BookingFor.BOOKING_FOR_OTHER,
          then: Yup.string()
            .required(t('first_name_required'))
            .min(2, t('field_min_length', { length: 2 }))
            .max(100, t('field_max_length', { length: 100 }))
            .nullable(),
        }),
      patient_lastName: Yup.string()
        .onlyAllowedChars(t('invalid_char'))
        .when('bookingFor', {
          is: BookingFor.BOOKING_FOR_OTHER,
          then: Yup.string()
            .required(t('last_name_required'))
            .min(2, t('field_min_length', { length: 2 }))
            .max(100, t('field_max_length', { length: 100 }))
            .nullable(),
        }),
      patient_birthdate: Yup.string().when('bookingFor', {
        is: BookingFor.BOOKING_FOR_OTHER,
        then: Yup.string().validateBirthdate('', {
          birthdateFormat,
          t,
          bookingFor: BookingFor.BOOKING_FOR_OTHER,
          isChildProphylaxis,
          dateOfAppointment,
        }),
      }),
      phone: Yup.string()
        .nullable()
        .test('is-valid', t('invalid_phone'), (value): any => {
          try {
            return parsePhoneNumberFromString(value || '')?.isValid()
          } catch (_) {
            return false
          }
        })
        .required(t('phone_required')),
      email: Yup.string()
        .nullable()
        .customEmail(t('invalid_char'))
        .required(t('email_required'))
        .email(t('invalid_email')),
      consent: Yup.bool().nullable(),
      dentalTosAccepted: Yup.bool().nullable().oneOf([true], t('data_usage_required')),
      insurance_holder: Yup.string().when('bookingFor', {
        is: BookingFor.BOOKING_FOR_OTHER,
        then: Yup.string().required(t('insurance_holder_required')),
      }),
      birthdate: Yup.string().when('bookingFor', {
        is: BookingFor.BOOKING_FOR_ME,
        then: Yup.string().validateBirthdate('', {
          birthdateFormat,
          t,
          bookingFor: BookingFor.BOOKING_FOR_ME,
          isChildProphylaxis,
          dateOfAppointment,
        }),
      }),
      reasonForVisit: isManualReviewRequired
        ? Yup.string()
            .min(5, t('field_min_length', { length: 5 }))
            .max(200, t('field_max_length', { length: 200 }))
            .required(t('reason_for_visit_required'))
        : Yup.string(),
    })
  }, [t, isManualReviewRequired, birthdateFormat, isChildProphylaxis, originalStartTimestamp])
  return validation
}

const DATE_VALIDATION_TRANSLATIONS = {
  EMPTY: 'date_validation_empty',
  INVALID_FORMAT: 'date_validation_format',
  TOO_OLD: 'date_validation_too_old',
  TOO_YOUNG: {
    [BookingFor.BOOKING_FOR_ME]: 'date_validation_too_young_18',
    [BookingFor.BOOKING_FOR_OTHER]: 'date_validation_too_young_1',
  },
}

export default useContactFormValidation
